import { Cells } from 'common/types/scalarSpreadsheet';
import { EvaluatedCellMap, EvaluatedCellSheetMap, ICell } from 'models/interfaces/spreadsheet-types/core-types';
import computeSourceRowsTotals from './computeSourceRowsTotals';
import { SourceTableData } from './customParser/types';
import customReverseParser from './customReverseParser/customReverseParser';
import { getCellByCustomKey } from './getCellByAlias';
import {
  SOURCE_ROW_PRICE,
  SOURCE_ROW_WEIGHTING,
  SOURCE_TOTALS_ROW,
  SOURCE_TOTALS_ROW_TARGET,
} from './sheetAliases';

type SourceRowsChange = { cell: ICell; value: unknown };

const isPriceOrWeightingCell = (alias: string | undefined): boolean =>
  Boolean(
    alias?.startsWith(`${SOURCE_ROW_PRICE}_`) || alias?.startsWith(`${SOURCE_ROW_WEIGHTING}_`)
  );

// afterCellChanged runs before onChange commits values into cells; overlay pending edits so totals stay in sync.
const overlayCellsWithPendingChanges = (cells: Cells, pendingChanges: SourceRowsChange[]): Cells => {
  const pendingByKey = new Map(
    pendingChanges.map(({ cell, value }) => [cell.key, value])
  );

  return Object.fromEntries(
    Object.entries(cells).map(([key, cell]) => {
      if (!pendingByKey.has(key)) {
        return [key, cell];
      }
      return [key, { ...cell, value: pendingByKey.get(key) }];
    })
  ) as Cells;
};

const afterCellChanged = (
  changes: SourceRowsChange[],
  cells: Cells | EvaluatedCellMap,
  _rowConfig: unknown,
  tableData: SourceTableData,
  _workbookCells?: EvaluatedCellSheetMap
): SourceRowsChange[] => {
  const shouldRecomputeTotals = changes.some(({ cell }) => isPriceOrWeightingCell(cell.alias));

  if (!shouldRecomputeTotals) {
    return changes;
  }

  const cellsWithPendingValues = overlayCellsWithPendingChanges(cells as Cells, changes);
  const rows = customReverseParser({
    cells: cellsWithPendingValues,
    tableData,
    fieldAttributes: tableData.fieldAttributes ?? {},
    allowEmptyValues: true,
  });
  const { weightedTargetPrice, totalWeighting } = computeSourceRowsTotals(rows);

  const totalsTargetCell = getCellByCustomKey(cells as Cells, SOURCE_TOTALS_ROW_TARGET);
  const totalsWeightingCell = getCellByCustomKey(cells as Cells, `${SOURCE_TOTALS_ROW}_weighting`);

  if (totalsTargetCell) {
    changes.push({ cell: totalsTargetCell, value: weightedTargetPrice });
  }
  if (totalsWeightingCell) {
    changes.push({ cell: totalsWeightingCell, value: totalWeighting });
  }

  return changes;
};

export default afterCellChanged;
